/**
 * GIESTO — product card gallery.
 *
 * Drives the previous/next arrows on `snippets/giesto-store-card.liquid` and
 * keeps their disabled state in step with the scroll position.
 *
 * This lives in a shared asset rather than inside a section's custom element
 * because more than one section renders the card. It used to sit in
 * `<giesto-store-collection>`, which meant the arrows were dead on the sale
 * page: that page has no such element, so nothing was listening and clicking an
 * arrow did nothing at all — no error, just a control that ignored you.
 *
 * Delegated from the document, so cards added later — an infinite-scroll batch,
 * a cart-drawer recommendation, a section re-render — are covered without any
 * re-binding.
 */
(() => {
  const GALLERY = '[data-gac-gallery]';
  const PREV = '[data-gac-gallery-prev]';
  const NEXT = '[data-gac-gallery-next]';
  const COLOUR_ROW = '.gac-card__colour-groups .csga-row';
  const COLOUR_LIMIT = 3;

  // The snippet is rendered on several page types, and each section that uses
  // it loads this file, so a second execution is possible.
  if (window.__giestoStoreCardGallery) return;
  window.__giestoStoreCardGallery = true;

  /**
   * Swatch Sync normally honours maxVisibleSwatches, but its async re-render
   * and a few older group payloads can still return the entire linked family.
   * Normalise the final DOM so every card has the same three-chip threshold.
   * The extra anchors stay in the row (CSS hides them) so the app can safely
   * re-render or update its active colour without losing product data.
   */
  const normaliseColourRow = (row) => {
    const swatches = [...row.querySelectorAll(':scope > .csw')];
    let more = row.querySelector(':scope > .csga-more');
    const overflow = Math.max(0, swatches.length - COLOUR_LIMIT);

    swatches.forEach((swatch, index) => {
      const hidden = index >= COLOUR_LIMIT;
      swatch.classList.toggle('csga-hide', hidden);
      swatch.toggleAttribute('hidden', hidden);
      swatch.setAttribute('aria-hidden', String(hidden));
    });

    if (!overflow) {
      more?.remove();
      return;
    }

    if (!more) {
      more = document.createElement('button');
      more.type = 'button';
      more.className = 'csga-more';
      row.append(more);
    }

    const label = `+${overflow}`;
    if (more.textContent !== label) more.textContent = label;
    more.setAttribute('aria-label', label);
  };

  const normaliseColourRows = (root = document) => {
    if (root instanceof Element && root.matches(COLOUR_ROW)) normaliseColourRow(root);
    root.querySelectorAll?.(COLOUR_ROW).forEach(normaliseColourRow);
  };

  window.GiestoStoreCards = Object.assign(window.GiestoStoreCards || {}, { normaliseColourRows });

  normaliseColourRows();
  new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes' && record.target instanceof Element) {
        normaliseColourRows(record.target.closest(COLOUR_ROW) || record.target);
      }
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) normaliseColourRows(node);
      });
    });
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });

  /** @param {Element} gallery */
  const syncArrows = (gallery) => {
    const card = gallery.closest('.gac-card');
    if (!card) return;

    // A fractional scrollLeft is normal mid-snap; a pixel of tolerance keeps the
    // ends from reading as "not quite there".
    const atStart = gallery.scrollLeft <= 1;
    const atEnd = gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 1;

    card.querySelector(PREV)?.toggleAttribute('disabled', atStart);
    card.querySelector(NEXT)?.toggleAttribute('disabled', atEnd);
  };

  window.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const moreColours = target.closest('.gac-card__colour-groups .csga-more');
    if (!moreColours) return;

    // The +N control is Swatch Sync's, not the reference's — the reference has
    // no such chip on a card at all. By request it behaves like the rest of the
    // card and opens the product, where every colour is listed, rather than
    // expanding the remaining swatches in place.
    // Capture phase is intentional: it runs before the app's own listener,
    // which would otherwise expand the row.
    event.preventDefault();
    event.stopImmediatePropagation();

    const href = moreColours.closest('.gac-card')?.querySelector('a[href*="/products/"]')?.href;
    if (href) window.location.assign(href);
  }, { capture: true });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const prev = target.closest(PREV);
    const next = target.closest(NEXT);
    if (!prev && !next) return;

    // The arrows sit inside the card's product link.
    event.preventDefault();

    const gallery = target.closest('.gac-card')?.querySelector(GALLERY);
    if (!(gallery instanceof HTMLElement)) return;

    const amount = next ? gallery.clientWidth : -gallery.clientWidth;
    const before = gallery.scrollLeft;
    gallery.scrollBy({ left: amount, behavior: 'smooth' });

    // A smooth programmatic scroll can be dropped outright on a mandatory-snap
    // scroller — measured on this gallery: scrollLeft stayed at 0 for both
    // scrollBy and scrollTo with `behavior: 'smooth'`, with and without the snap
    // temporarily disabled, while the same call at the default behaviour moved
    // it the full width. Rather than leave the arrow looking dead when that
    // happens, check a beat later and jump. Where the animation does run this
    // sees the scroller already moving and does nothing.
    setTimeout(() => {
      if (Math.abs(gallery.scrollLeft - before) < 1) gallery.scrollBy({ left: amount });
    }, 250);
  });

  // Scroll does not bubble, but a capture-phase listener on the document still
  // sees it, so one listener covers every gallery on the page.
  document.addEventListener(
    'scroll',
    (event) => {
      const gallery = event.target;
      if (gallery instanceof HTMLElement && gallery.matches(GALLERY)) syncArrows(gallery);
    },
    { capture: true, passive: true }
  );

  // Frame videos behave like moving photos: muted, no controls, playing only
  // while at least half on screen. Reduced motion and data saver keep them on
  // their poster frame.
  const videos = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const still = () => motion.matches || Boolean(navigator.connection?.saveData);
  const release = video => {
    video.pause();
    if (video.hasAttribute('data-gac-video-unloaded') || (!video.currentSrc && video.readyState === 0)) return;
    // Store sources on the elements: editorial view clones grid cards, and
    // those clones must be able to restore a previously unloaded video too.
    video.setAttribute('data-gac-video-unloaded', '');
    if (video.getAttribute('src')) video.dataset.gacVideoSrc = video.getAttribute('src');
    video.removeAttribute('src');
    for (const source of video.querySelectorAll('source[src]')) {
      source.dataset.gacVideoSrc = source.getAttribute('src');
      source.removeAttribute('src');
    }
    // Pausing alone retains every visited card's media buffer in a long list.
    // Reset the resource selection to release it, keeping the poster visible.
    video.load();
  };
  const restore = video => {
    if (!video.hasAttribute('data-gac-video-unloaded')) return;
    video.removeAttribute('data-gac-video-unloaded');
    if (video.dataset.gacVideoSrc) {
      video.setAttribute('src', video.dataset.gacVideoSrc);
      delete video.dataset.gacVideoSrc;
    }
    for (const source of video.querySelectorAll('source[data-gac-video-src]')) {
      source.setAttribute('src', source.dataset.gacVideoSrc);
      delete source.dataset.gacVideoSrc;
    }
    video.load();
  };
  const update = (video, visible) => {
    if (visible && !document.hidden && !still()) {
      restore(video);
      video.muted = true;
      video.play()?.catch(() => {});
    } else video.pause();
  };
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!videos.has(entry.target)) continue;
      const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      videos.set(entry.target, visible);
      update(entry.target, visible);
    }
  }, { threshold: [0, 0.5] });
  // Keep nearby paused videos warm, but don't accumulate buffers for the
  // entire sale catalog. This also covers dynamically mounted editorial cards.
  const rangeObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (videos.has(entry.target) && !entry.isIntersecting) release(entry.target);
    }
  }, { rootMargin: '1000px' });
  const discover = () => {
    for (const [video] of videos) {
      if (video.isConnected) continue;
      release(video);
      observer.unobserve(video);
      rangeObserver.unobserve(video);
      videos.delete(video);
    }
    for (const video of document.querySelectorAll('video.gac-frame-video')) {
      if (videos.has(video)) continue;
      video.playsInline = true;
      videos.set(video, false);
      observer.observe(video);
      rangeObserver.observe(video);
    }
  };
  let discoveryPending = false;
  new MutationObserver(() => {
    if (discoveryPending) return;
    discoveryPending = true;
    queueMicrotask(() => { discoveryPending = false; discover(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  const refresh = () => { for (const [video, visible] of videos) update(video, visible); };
  document.addEventListener('visibilitychange', refresh);
  motion.addEventListener('change', refresh);
  discover();
})();
